import { 
  collection, 
  query, 
  getDocs, 
  where, 
  doc, 
  getDoc,
  addDoc,
  Timestamp,
  increment,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';

export interface TemplateUsageStats {
  templateId: string;
  templateName: string;
  category: string;
  totalUses: number;
  uniqueUsers: number;
  lastUsed: Date | null;
  createdBy: string;
  createdAt: Date;
  downloadCount: number;
  isActive: boolean;
  usageByUser: Array<{
    userId: string;
    userEmail: string;
    useCount: number;
    lastUsed: Date;
  }>;
  usageByDate: Array<{
    date: string;
    uses: number;
  }>;
}

export interface TemplateAnalytics {
  totalTemplates: number;
  activeTemplates: number;
  inactiveTemplates: number;
  templatesByCategory: Record<string, number>;
  mostUsedTemplates: TemplateUsageStats[];
  recentlyCreated: TemplateUsageStats[];
  recentlyUsed: TemplateUsageStats[];
  usageOverTime: Array<{
    date: string;
    totalUses: number;
    uniqueUsers: number;
  }>;
  topCategories: Array<{
    category: string;
    count: number;
    usage: number;
  }>;
}

export interface TemplateUser {
  userId: string;
  email: string;
  name?: string;
  totalTemplatesUsed: number;
  lastActive: Date;
  templatesUsed: Array<{
    templateId: string;
    templateName: string;
    useCount: number;
    lastUsed: Date;
  }>;
}

class TemplateAnalyticsService {
  // Track template usage
  async trackTemplateUsage(
    templateId: string, 
    userId: string, 
    userEmail: string,
    action: 'view' | 'copy' | 'use' | 'download' = 'use'
  ): Promise<void> {
    try {
      const usageData = {
        templateId,
        userId,
        userEmail,
        action,
        timestamp: Timestamp.now(),
        date: new Date().toISOString().split('T')[0] // YYYY-MM-DD format
      };

      // Add to usage tracking collection
      await addDoc(collection(db, 'template_usage'), usageData);

      // Update template usage count
      const templateRef = doc(db, 'email_templates', templateId);
      await updateDoc(templateRef, {
        usageCount: increment(1),
        lastUsed: Timestamp.now()
      });

      console.log('Template usage tracked:', usageData);
    } catch (error) {
      console.error('Error tracking template usage:', error);
    }
  }

  // Get comprehensive template analytics
  async getTemplateAnalytics(period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<TemplateAnalytics> {
    try {
      const endDate = new Date();
      const startDate = new Date();
      
      switch (period) {
        case '7d':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(endDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(endDate.getDate() - 90);
          break;
        case '1y':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
      }

      // Get all templates
      const templatesQuery = query(collection(db, 'email_templates'));
      const templatesSnapshot = await getDocs(templatesQuery);
      const templates = templatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];

      // Get usage data
      const usageQuery = query(
        collection(db, 'template_usage'),
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        where('timestamp', '<=', Timestamp.fromDate(endDate))
      );
      const usageSnapshot = await getDocs(usageQuery);
      const usageData = usageSnapshot.docs.map(doc => doc.data());

      // Calculate analytics
      const totalTemplates = templates.length;
      const activeTemplates = templates.filter(t => t.isActive).length;
      const inactiveTemplates = totalTemplates - activeTemplates;

      // Templates by category
      const templatesByCategory = templates.reduce((acc, template) => {
        acc[template.category] = (acc[template.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Most used templates
      const templateUsageMap = new Map<string, {
        templateId: string;
        templateName: string;
        category: string;
        totalUses: number;
        uniqueUsers: Set<string>;
        lastUsed: Date | null;
        createdBy: string;
        createdAt: Date;
        downloadCount: number;
        isActive: boolean;
      }>();

      usageData.forEach(usage => {
        const template = templates.find(t => t.id === usage.templateId);
        if (template) {
          if (!templateUsageMap.has(usage.templateId)) {
            templateUsageMap.set(usage.templateId, {
              templateId: usage.templateId,
              templateName: template.name,
              category: template.category,
              totalUses: 0,
              uniqueUsers: new Set(),
              lastUsed: null,
              createdBy: template.createdBy,
              createdAt: template.createdAt?.toDate() || new Date(),
              downloadCount: template.downloadCount || 0,
              isActive: template.isActive
            });
          }
          
          const stats = templateUsageMap.get(usage.templateId)!;
          stats.totalUses++;
          stats.uniqueUsers.add(usage.userId);
          stats.lastUsed = usage.timestamp?.toDate() || new Date();
        }
      });

      const mostUsedTemplates = Array.from(templateUsageMap.values())
        .map(stats => ({
          ...stats,
          uniqueUsers: stats.uniqueUsers.size,
          usageByUser: [],
          usageByDate: []
        }))
        .sort((a, b) => b.totalUses - a.totalUses)
        .slice(0, 10);

      // Recently created templates
      const recentlyCreated = templates
        .filter(t => t.isActive)
        .sort((a, b) => {
          const aDate = a.createdAt?.toDate() || new Date(0);
          const bDate = b.createdAt?.toDate() || new Date(0);
          return bDate.getTime() - aDate.getTime();
        })
        .slice(0, 10)
        .map(template => ({
          templateId: template.id,
          templateName: template.name,
          category: template.category,
          totalUses: 0,
          uniqueUsers: 0,
          lastUsed: null,
          createdBy: template.createdBy,
          createdAt: template.createdAt?.toDate() || new Date(),
          downloadCount: template.downloadCount || 0,
          isActive: template.isActive,
          usageByUser: [],
          usageByDate: []
        }));

      // Recently used templates
      const recentlyUsed = Array.from(templateUsageMap.values())
        .filter(stats => stats.lastUsed)
        .sort((a, b) => {
          const aDate = a.lastUsed || new Date(0);
          const bDate = b.lastUsed || new Date(0);
          return bDate.getTime() - aDate.getTime();
        })
        .slice(0, 10)
        .map(stats => ({
          ...stats,
          uniqueUsers: stats.uniqueUsers.size,
          usageByUser: [],
          usageByDate: []
        }));

      // Usage over time
      const usageByDate = new Map<string, { totalUses: number; uniqueUsers: Set<string> }>();
      usageData.forEach(usage => {
        const date = usage.date;
        if (!usageByDate.has(date)) {
          usageByDate.set(date, { totalUses: 0, uniqueUsers: new Set() });
        }
        const dayStats = usageByDate.get(date)!;
        dayStats.totalUses++;
        dayStats.uniqueUsers.add(usage.userId);
      });

      const usageOverTime = Array.from(usageByDate.entries())
        .map(([date, stats]) => ({
          date,
          totalUses: stats.totalUses,
          uniqueUsers: stats.uniqueUsers.size
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Top categories
      const categoryUsage = new Map<string, { count: number; usage: number }>();
      Object.entries(templatesByCategory).forEach(([category, count]) => {
        const categoryTemplates = templates.filter(t => t.category === category);
        const totalUsage: number = categoryTemplates.reduce((sum, t) => {
          const usageCount = typeof t.usageCount === 'number' ? t.usageCount : 0;
          return sum + usageCount;
        }, 0);
        const countNumber = typeof count === 'number' ? count : 0;
        categoryUsage.set(category, { count: countNumber, usage: totalUsage });
      });

      const topCategories = Array.from(categoryUsage.entries())
        .map(([category, stats]) => ({
          category,
          count: stats.count,
          usage: stats.usage
        }))
        .sort((a, b) => b.usage - a.usage)
        .slice(0, 10);

      return {
        totalTemplates,
        activeTemplates,
        inactiveTemplates,
        templatesByCategory,
        mostUsedTemplates,
        recentlyCreated,
        recentlyUsed,
        usageOverTime,
        topCategories
      };
    } catch (error) {
      console.error('Error getting template analytics:', error);
      throw new Error('Failed to get template analytics');
    }
  }

  // Get detailed usage stats for a specific template
  async getTemplateUsageStats(templateId: string): Promise<TemplateUsageStats | null> {
    try {
      // Get template info
      const templateRef = doc(db, 'email_templates', templateId);
      const templateSnap = await getDoc(templateRef);
      
      if (!templateSnap.exists()) {
        return null;
      }
      
      const template = templateSnap.data();

      // Get usage data for this template
      const usageQuery = query(
        collection(db, 'template_usage'),
        where('templateId', '==', templateId)
      );
      const usageSnapshot = await getDocs(usageQuery);
      const usageData = usageSnapshot.docs.map(doc => doc.data());

      // Calculate stats
      const uniqueUsers = new Set(usageData.map(u => u.userId));
      const usageByUser = new Map<string, { count: number; lastUsed: Date }>();
      
      usageData.forEach(usage => {
        const userId = usage.userId;
        if (!usageByUser.has(userId)) {
          usageByUser.set(userId, { count: 0, lastUsed: usage.timestamp?.toDate() || new Date() });
        }
        const userStats = usageByUser.get(userId)!;
        userStats.count++;
        const usageDate = usage.timestamp?.toDate() || new Date();
        if (usageDate > userStats.lastUsed) {
          userStats.lastUsed = usageDate;
        }
      });

      // Get user details (simplified - in real app you'd fetch from users collection)
      const usageByUserArray = Array.from(usageByUser.entries()).map(([userId, stats]) => ({
        userId,
        userEmail: `user-${userId}@example.com`, // Simplified
        useCount: stats.count,
        lastUsed: stats.lastUsed
      }));

      // Usage by date
      const usageByDate = new Map<string, number>();
      usageData.forEach(usage => {
        const date = usage.date;
        usageByDate.set(date, (usageByDate.get(date) || 0) + 1);
      });

      const usageByDateArray = Array.from(usageByDate.entries())
        .map(([date, uses]) => ({ date, uses }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return {
        templateId,
        templateName: template.name,
        category: template.category,
        totalUses: usageData.length,
        uniqueUsers: uniqueUsers.size,
        lastUsed: usageData.length > 0 
          ? new Date(Math.max(...usageData.map(u => u.timestamp?.toDate().getTime() || 0)))
          : null,
        createdBy: template.createdBy,
        createdAt: template.createdAt?.toDate() || new Date(),
        downloadCount: template.downloadCount || 0,
        isActive: template.isActive,
        usageByUser: usageByUserArray,
        usageByDate: usageByDateArray
      };
    } catch (error) {
      console.error('Error getting template usage stats:', error);
      throw new Error('Failed to get template usage stats');
    }
  }

  // Get all users who have used templates
  async getTemplateUsers(): Promise<TemplateUser[]> {
    try {
      const usageQuery = query(collection(db, 'template_usage'));
      const usageSnapshot = await getDocs(usageQuery);
      const usageData = usageSnapshot.docs.map(doc => doc.data());

      const userMap = new Map<string, {
        userId: string;
        email: string;
        totalTemplatesUsed: number;
        lastActive: Date;
        templatesUsed: Map<string, { useCount: number; lastUsed: Date }>;
      }>();

      usageData.forEach(usage => {
        const userId = usage.userId;
        if (!userMap.has(userId)) {
          userMap.set(userId, {
            userId,
            email: usage.userEmail,
            totalTemplatesUsed: 0,
            lastActive: usage.timestamp?.toDate() || new Date(),
            templatesUsed: new Map()
          });
        }

        const user = userMap.get(userId)!;
        user.totalTemplatesUsed++;
        
        const usageDate = usage.timestamp?.toDate() || new Date();
        if (usageDate > user.lastActive) {
          user.lastActive = usageDate;
        }

        const templateId = usage.templateId;
        if (!user.templatesUsed.has(templateId)) {
          user.templatesUsed.set(templateId, { useCount: 0, lastUsed: usageDate });
        }
        
        const templateStats = user.templatesUsed.get(templateId)!;
        templateStats.useCount++;
        if (usageDate > templateStats.lastUsed) {
          templateStats.lastUsed = usageDate;
        }
      });

      return Array.from(userMap.values()).map(user => ({
        userId: user.userId,
        email: user.email,
        totalTemplatesUsed: user.totalTemplatesUsed,
        lastActive: user.lastActive,
        templatesUsed: Array.from(user.templatesUsed.entries()).map(([templateId, stats]) => ({
          templateId,
          templateName: `Template ${templateId}`, // Simplified
          useCount: stats.useCount,
          lastUsed: stats.lastUsed
        }))
      }));
    } catch (error) {
      console.error('Error getting template users:', error);
      throw new Error('Failed to get template users');
    }
  }
}

export const templateAnalyticsService = new TemplateAnalyticsService();
